import { genericMemo } from "@redotech/react-util/component";
import { SchemaBooleanExpression } from "@redotech/redo-flows-core-schema-common/expressions";
import {
  GenericDataType,
  genericPrefixes,
  removeGenericFromType,
} from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import { ReactNode } from "react";
import { ArrayInspector } from "./array-inspector";
import { BooleanInspector } from "./boolean-inspector";
import { CheckoutContextPredicateInspector } from "./checkout-context-predicate-inspector";
import { EnumInspector } from "./enum-inspector";
import {
  arrayPredicate,
  booleanEvaluation,
  checkoutContextPredicate,
  numberComparison,
  textMatch,
} from "./expression-of-type";
import { InspectorKind, inspectorKindByDataType } from "./inspector-kind";
import { NumberInspector } from "./number-inspector";
import { TextInspector } from "./text-inspector";
import { TokenInspector } from "./token-inspector";

interface FieldInspectorProps {
  schema: Schema;
  selectedField: Schema["fields"][string];
  /** `field` names the variable being matched, so it outlives a type reset. */
  expression: DeepPartialExcept<SchemaBooleanExpression, "field">;
  setExpression: (expression: DeepPartial<SchemaBooleanExpression>) => void;
  showValidationErrors?: boolean;
}

const inspectors: Record<
  InspectorKind,
  (props: FieldInspectorProps) => ReactNode
> = {
  [InspectorKind.TEXT]: (p) => (
    <TextInspector {...p} expression={textMatch(p.expression)} />
  ),
  [InspectorKind.ENUM]: (p) => (
    <EnumInspector {...p} expression={textMatch(p.expression)} />
  ),
  [InspectorKind.TOKEN]: (p) => (
    <TokenInspector {...p} expression={textMatch(p.expression)} />
  ),
  [InspectorKind.BOOLEAN]: (p) => (
    <BooleanInspector {...p} expression={booleanEvaluation(p.expression)} />
  ),
  [InspectorKind.NUMBER]: (p) => (
    <NumberInspector {...p} expression={numberComparison(p.expression)} />
  ),
  [InspectorKind.CHECKOUT_CONTEXT_PREDICATE]: (p) => (
    <CheckoutContextPredicateInspector
      {...p}
      expression={checkoutContextPredicate(p.expression)}
    />
  ),
};

export const FieldInspector = genericMemo(function FieldInspector({
  dataType,
  ...props
}: FieldInspectorProps & { dataType: GenericDataType }) {
  const { t } = useTranslation();

  if (dataType.startsWith(genericPrefixes.Array)) {
    return (
      <ArrayInspector
        {...props}
        dataType={dataType}
        expression={arrayPredicate(props.expression)}
      />
    );
  }

  const kind = inspectorKindByDataType[removeGenericFromType(dataType)];
  if (!kind) {
    const unsupportedDataType = (console.error(dataType), dataType);
    return (
      <>
        {t("Unsupported step type {{dataType}}", {
          dataType: unsupportedDataType,
        })}
      </>
    );
  }
  return inspectors[kind](props);
});
